import { getByID } from '@adornis/baseql/operations/mongo.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { GlobalSetting } from '@adornis/buchungsheld-db/entities/global-setting.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import '@adornis/popover/x-dropdown.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import logo from '../assets/logo.png';

/**
 * @element bh-topbar
 */
@customElement('bh-topbar')
export class BHTopbar extends BuchungsheldLitElement {
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          backdropFilter: 'blur(4px) grayscale(1)',
          background: 'rgb(255 255 255 / 96%)',
          boxShadow: `${this.colors.black.opacity(0.1)} 4px 4px 40px`,
          position: 'sticky',
          top: '0px',
          zIndex: '999999',
        },
      },
    ];
  }
  @property() page?: string;

  @state() private readonly _currentUser = new RXController(this, currentUser<User>());
  @state() private readonly _language = new RXController(this, globalLanguageSubject);
  @state() private readonly _global = new RXController(this, GlobalSetting.getGlobalSetting()({ logoID: 1 }));
  @state() private readonly _institutionID = new RXController(this, routingState.searchParams.get('institution'));
  @state() private readonly _institutionImage = new RXController(
    this,
    this._institutionID.observable.pipe(
      filter(Boolean),
      distinctUntilChanged(),
      switchMap(instID =>
        getByID(
          Institution,
          instID,
        )({
          logoID: 1,
        }),
      ),
      filter(Boolean),
      map(institute => institute.logoID),
    ),
  );

  override render() {
    const items = (() => {
      if (this.clientWidth > 500) return [this._loginLogout(), this._home(), this._profile(), this._admin()];

      const dropdown_ref = createRef<XDropdown>();
      return html`
        <tr-button mode="icon" icon="menu" @click=${() => dropdown_ref.value?.toggle()}>Filter</tr-button>
        <x-dropdown ${ref(dropdown_ref)}>
          <x-flex slot="dropdown-content">
            <x-card>
              <x-flex vertical crossaxis-start ${css({ gap: '14px' })}>
                ${[this._home(true), this._profile(true), this._admin(true), this._loginLogout()]}
              </x-flex>
            </x-card>
          </x-flex>
        </x-dropdown>
      `;
    })();

    return html`
      <x-section>
        <x-flex horizontal end crossaxis-center space="sm" ${css({ padding: `${this.spacing.md} 0` })}>
          ${this._logo()} ${this._languageSwitch()} ${items}
        </x-flex>
      </x-section>
    `;
  }

  private _languageSwitch() {
    return html`
      <x-button
        mode="text"
        ${css({ textTransform: 'uppercase' })}
        @click=${() =>
          globalLanguageSubject.next(this._language.value === Language.GERMAN ? Language.ENGLISH : Language.GERMAN)}
      >
        ${this._language.value}
      </x-button>
    `;
  }

  private _loginLogout() {
    return this._currentUser.isLoading
      ? html` <tr-text> ... </tr-text>`
      : this._currentUser.value
      ? html`
          <tr-button mode="text" icon="logout" @click=${() => logout()}>
            ${this._translation.translate('LOGOUT')}
          </tr-button>
        `
      : html`
          <tr-button mode="text" icon="login" href="/profile"> ${this._translation.translate('LOGIN')} </tr-button>
        `;
  }

  private _profile(withText?: Boolean) {
    if (!this._currentUser.value || this.page === 'profile') return nothing;
    const text = withText ? this._translation.translate('admin') : '';
    return html` <tr-button mode="text" icon="person" href="/profile">${text}</tr-button> `;
  }

  private _admin(withText?: Boolean) {
    if (!this._currentUser.value?.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, ANY_CONTEXT)) return nothing;
    const text = withText ? this._translation.translate('admin') : '';
    return html` <tr-button mode="text" icon="settings" href="/admin">${text}</tr-button> `;
  }

  private _home(withText?: boolean) {
    if (!this.page) return nothing;
    const text = withText ? this._translation.translate('home') : '';
    return html` <tr-button mode="text" icon="home" href="/">${text}</tr-button> `;
  }

  private _logo() {
    if (this.isInIframe()) return nothing;

    const logoID = this._global.value?.logoID;
    const institutImageID = this._institutionImage.value;
    const displayInstitutImage = this.page === 'registration' && institutImageID;

    return html`
      <x-link href="/" ${css({ 'margin-right': displayInstitutImage ? 'unset' : 'auto' })}>
        <img
          src="${logoID ? AdornisFile.getServeLink(logoID) : logo}"
          ${css({ height: '3em', marginTop: `-${this.spacing.xs}` })}
        />
      </x-link>

      ${displayInstitutImage
        ? html`
            <x-flex horizontal center crossaxis-center ${css({ marginRight: 'auto' })}>
              <x-icon ${css({ margin: '0 .25em 0 .5em' })}>arrow_right</x-icon>

              <x-link href="/">
                <img
                  .src=${institutImageID ? AdornisFile.getServeLink(institutImageID) : logo}
                  ${css({ height: '2.5em', margin: '.25em 0' })}
                />
              </x-link>
            </x-flex>
          `
        : nothing}
    `;
  }

  private isInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
}
